<template>
	<div class="table-overflow-hide">
		<div class="directoryTable scrollable-55vh">
			<table class="table">
				<thead class="sticky-header">
					<tr>
						<th class="icon-col"></th>
						<SortHeader
							title="name"
							:asc="currentSort.asc"
							:sort-key="currentSort.key"
							@click="() => setSort('name')"
							data-cy="report-table-header"
							class="header-cell"
							color="#695b59"
							weight="500"
						></SortHeader>
						<th class="icon-col">&nbsp;</th>
						<th>email</th>
						<th>phone</th>
						<th class="icon-col">
							<v-btn
								@click="parentsS.parents.reload()"
								icon
								color="gray200"
								size="x-small"
								:loading="parentsS.parents.loading"
								style="max-width: 20px; max-height: 20px"
							>
								<v-icon>mdi-reload</v-icon></v-btn
							>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="row in paginatedResults"
						:key="row.uid"
						:class="{
							'table-row': true,
							selected:
								(editModal.show && editModal.parent === row) ||
								(editRelationModal.show && editRelationModal.parent === row),
						}"
					>
						<td>
							<v-btn
								color="gray300"
								density="compact"
								icon
								variant="text"
								@click="updatePinUser(row)"
								:loading="state.loadPinned.includes(row.uid)"
								:disabled="deleted || !canEditDirectory"
							>
								<v-tooltip location="bottom" class="custom-tooltip" activator="parent">
									Lock Record
								</v-tooltip>
								<v-icon :color="row.pinned ? 'primary' : 'gray500'"
									>mdi-lock-outline</v-icon
								>
							</v-btn>
						</td>
						<td data-cy="table-col-name">
							<!-- <span class="d-flex align-center justify-content-center gap-3"> -->
							{{ row.firstname }} {{ row.lastname }}
							<v-btn
								@click="
									editModal.show = true;
									editModal.parent = row;
								"
								color="gray300"
								density="compact"
								icon
								variant="text"
								:disabled="!canEditDirectory"
							>
								<v-icon color="gray500">mdi-note-edit</v-icon>
							</v-btn>
							<!-- </span> -->
						</td>
						<td>
							<v-btn
								data-cy="directory-impersonate-btn"
								@click="
									impersonate(row.uid, schoolStore?.school?.school_id || row.school_id)
								"
								:loading="state.loadImpersonate.includes(row.uid)"
								color="gray300"
								density="compact"
								icon
								variant="text"
								:disabled="deleted || !canEditDirectory"
							>
								<v-tooltip activator="parent" location="bottom"
									>Log in as this parent</v-tooltip
								>
								<v-icon color="gray500">mdi-security</v-icon>
							</v-btn>
						</td>
						<td data-cy="table-col-email">
							<!-- <span class="d-flex align-center justify-content-center"> -->
							{{ row.email }}
							<!-- </span> -->
						</td>
						<td data-cy="table-col-phone">
							{{ row.phone }}
						</td>
						<td>
							<v-btn
								@click="
									editRelationModal.show = true;
									editRelationModal.parent = row;
								"
								color="gray300"
								density="compact"
								icon
								variant="text"
								:disabled="deleted || !canEditDirectory"
								data-cy="edit-relations-btn"
							>
								<v-icon color="gray500">mdi-account-group</v-icon>
							</v-btn>
						</td>
					</tr>
				</tbody>
			</table>
			<div
				v-if="parentsS.parents.loading && !parentsS.parents.data.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<LoadingState text="Loading Parents"></LoadingState>
			</div>
			<div
				v-if="!parentsS.parents.loading && !parentsS.parents.data.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<div class="empty-state">
					<figure style="max-width: 300px">
						<IconListEmptyState />
					</figure>
					<div class="title">We couldn't find any Parents.</div>
					<p>Try reloading the page or adding a new parent.</p>
				</div>
			</div>
		</div>
	</div>
	<div class="table-pagination-btns">
		<pagination v-bind="forComponent()"></pagination>
		<div class="table-buttons" v-if="canEditDirectory">
			<v-btn @click="addModal.show = true" data-cy="directory-create-entity-btn"
				>Create New</v-btn
			>
			<v-btn
				@click="uploadModal.show = true"
				color="#E5E4DB"
				append-icon="mdi-tray-arrow-up"
				>Upload</v-btn
			>

			<div class="d-flex align-center">
				<v-btn
					@click="download"
					:loading="state.loadDownload"
					color="#E5E4DB"
					append-icon="mdi-tray-arrow-down"
					>Download</v-btn
				>
				<div class="ml-1" tabindex="1">
					<v-tooltip location="bottom" activator="parent">Download Relations</v-tooltip>
					<RelationsDownload :download-types="['relationship']"></RelationsDownload>
				</div>
			</div>
		</div>
	</div>
	<EditParentModal
		v-model:show="editModal.show"
		:parent="editModal.parent"
	></EditParentModal>
	<AddParentModal
		v-model:show="addModal.show"
		@update:search="
			$emit('update:search', $event);
			addModal.show = false;
		"
	></AddParentModal>
	<parent-relations-modal
		v-model:show="editRelationModal.show"
		:directory-entity="editRelationModal.parent"
		:entity-type="'parent'"
		:entity-id="editRelationModal.parent.uid"
		:entity-name="`${editRelationModal.parent.firstname} ${editRelationModal.parent.lastname}`"
		:tabs="['student', 'teacher', 'team']"
		@entity-click="handleEntityClick"
	></parent-relations-modal>

	<upload-csv-modal v-model:show="uploadModal.show" :tab="props.tab" />
</template>

<script setup lang="ts">
import { reactive, watch } from "vue";
import usePaginate from "@/hooks/usePaginate";
import { computed } from "vue";
import pagination from "@/components/pagination.vue";
import downloadFile, { complexSearch } from "@/utils";
import type { GuardiansType } from "@/api_types/school";
import EditParentModal from "./editParentModal.vue";
import AddParentModal from "./addParentModal.vue";
// import ParentRelationsModal from "./ParentRelationsModal.vue";
import ParentRelationsModal from "@/components/school/directory/RelationshipModal.vue";

import useParents from "@/stores/school/parents";
import FetchService from "@/services/fetch.service";
import { useAuthenticationStore } from "@/stores/authentication";
import authService from "@/services/auth.service";
import { useAlertsStore } from "@/stores/alerts";
import { useSchoolStore } from "@/stores/school";
import LoadingState from "@/components/loadingState.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
// @ts-ignore
import uploadCsvModal from "../uploadCsvModal.vue";
import RelationsDownload from "../relationsDownload.vue";
import useTableSort from "@/hooks/useTableSort";
import { sort } from "@/utils/sort";
import SortHeader from "@/components/global/sortHeader.vue";

const props = defineProps<{
	tab: "teacher" | "parent" | "student" | "team";
	search: string;
	deleted: boolean;
	selectedEntity?: any;
}>();
const emit = defineEmits<{
  (e: 'update:search', ...args: any[]): void;
  (e: 'entity-click', entityData: any): void;
}>();

const { currentSort, setSort } = useTableSort([
	{
		key: "name",
		title: "name",
		sortKey: "name",
	},
]);
const authStore = useAuthenticationStore();
const canEditDirectory = computed(() => authStore.hasPolicy("school.directory.parent.write"));
const schoolStore = useSchoolStore();
const parentsS = useParents();
parentsS.parents.reload();

const state = reactive({
	loadImpersonate: [] as string[],

	loadPinned: [] as string[],
	loadDownload: false,
});

const editModal = reactive({ show: false, parent: {} as GuardiansType[number] });
const addModal = reactive({ show: false });
const editRelationModal = reactive({ show: false, parent: {} as GuardiansType[number] });

const uploadModal = reactive({
	show: false,
});

const filteredData = computed(() => {
	return sort(
		parentsS.parents.data?.filter((i) => {
			if (i.deleted !== props.deleted) return false;
			return complexSearch(props.search || "", `${i.firstname} ${i.lastname} ${i.email}`);
		}) || [],
		(i) => `${i.lastname} ${i.firstname}`,
		currentSort.value.asc
	);
});

const { forComponent, paginatedResults } = usePaginate(filteredData);

const impersonate = async (parent_uid, school_id) => {
	// The school id should be the school id for the current school.  Therefore, if the user is logged into a district site, then the district school id should be passed in.  If the school id for the current school is not set for some reason, then it will fall back to the school id of the parent.  The row.school_id is not the ideal option.

	state.loadImpersonate.push(parent_uid);
	try {
		const res = await FetchService.fetchData(
			`/impersonate?uid=${parent_uid}&school_id=${school_id}&type=parent`,
			"get"
		);
		if (!res) throw "Failed";
		const data = authService.parseJwt(res, false);
		localStorage.setItem(authStore.getTokenName("parent"), JSON.stringify(data));
		window.open(`/${authStore.account}`, "_blank");
	} catch (error) {
		useAlertsStore().addAlert(
			"error",
			"Failed to impersonate parent, please try again later"
		);
	}
	state.loadImpersonate = state.loadImpersonate.filter((str) => str !== parent_uid);
};

const updatePinUser = async (parent: GuardiansType[number]) => {
	state.loadPinned.push(parent.uid);
	const res = await parentsS.updateParent(parent.uid, {
		pinned: !parent.pinned,
	});
	useAlertsStore().addAlert(
		res ? "success" : "error",
		res ? "Parent saved successfully" : "Failed to update parent",
		5
	);
	state.loadPinned = state.loadPinned.filter((str) => str !== parent.uid);
};

const download = async () => {
	state.loadDownload = true;
	const res = await FetchService.fetchData("/guardians/download", "get", undefined, true);
	const cType = res?.res?.headers.get?.("content-type") || "";
	if (typeof res?.data === "string" && cType.includes("text/csv")) {
		downloadFile(
			res.data,
			res?.res?.headers
				?.get("content-disposition")
				?.split("filename=")?.[1]
				?.replace(/"/g, "") || "parents.csv",
			"text/csv"
		);
	} else {
		useAlertsStore().addAlert(
			"error",
			"Failed to download parents, please try again later",
			10
		);
	}
	state.loadDownload = false;
};

// Handle entity clicks from relationship modal
const handleEntityClick = (entityData: any) => {
	emit('entity-click', entityData);
};

// Watch for selected entity changes and open the relationship modal
watch(() => props.selectedEntity, (newEntity) => {
	if (newEntity && newEntity.entityType === 'parent') {
		// Find the parent in the current data
		const parent = parentsS.parents.data.find(p => p.uid === newEntity.entityId);
		if (parent) {
			editRelationModal.parent = parent;
			editRelationModal.show = true;
		}
	}
}, { immediate: true });
</script>
<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;
.scrollable {
	max-height: 55vh;
	height: 55vh;
	overflow: scroll;
}

.icon-col {
	width: 0px;
}

.table-row {
	td {
		transition: background-color 0.3s;
	}
	&.selected {
		td {
			background-color: #e6e6e689 !important;
		}
	}
}
</style>
